import { useState, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Icon } from "@mui/material";

import {
  useIgnitionRegimes,
  useAddIgnitionRegime,
  useDeleteIgnitionRegime,
  useUpdateIgnitionRegime,
} from "../api/ignitionRegimes";

import { IgnitionRegime } from "../types/ignitionRegime";
import { IgnitionRegimeContext } from "../contexts/IgnitionRegimeContext";
import ScenarioSegmentButton from "./ScenarioSegmentButton";
import AddIgnitionRegimeModal from "./AddIgnitionRegimeModal";
import EditIgnitionRegimeModal from "./EditIgnitionRegimeModal";
import { useProject } from "../api/project";
import { bboxFromBoundary } from "../utils/boundaryUtils";
import { useAuth } from "../contexts/AuthContext";
import { isViewOnly } from "../utils/permissions";
import {
  ScenarioEmptyState,
  ScenarioList,
  ScenarioPopover,
  ScenarioRow,
  StatusLine,
} from "./scenarioBar/ScenarioPopover";
import { useUndoableDelete } from "./scenarioBar/useUndoableDelete";
import { UndoToast } from "./scenarioBar/UndoToast";

const pluralIgnitions = (n: number) => `${n} ${n === 1 ? "ignition" : "ignitions"}`;

export default function IgnitionRegimeDropdown() {
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const { projectId } = useParams();
  const projectIdNumber = Number(projectId) || 0;
  const queryClient = useQueryClient();

  const { project } = useProject();

  const { ignitionRegimes, isLoading, error } = useIgnitionRegimes(projectIdNumber);
  const { mutateAsync: addRegime } = useAddIgnitionRegime(projectIdNumber);
  const { mutate: deleteRegime } = useDeleteIgnitionRegime(projectIdNumber);
  const { mutate: updateRegime } = useUpdateIgnitionRegime(projectIdNumber);

  const { selectedRegime, setSelectedRegime } = useContext(IgnitionRegimeContext);
  const { pending, scheduleDelete, undo, dismiss } = useUndoableDelete();
  const regimesKey = [projectIdNumber, "ignitionRegimes"];

  useEffect(() => {
    if (!ignitionRegimes || ignitionRegimes.length === 0) {
      setSelectedRegime(null);
      return;
    }

    if (!selectedRegime) {
      setSelectedRegime(ignitionRegimes[0]);
      return;
    }

    // Update selectedRegime with fresh data from refetch
    const updatedRegime = ignitionRegimes.find((r) => r.id === selectedRegime.id);
    if (updatedRegime) {
      setSelectedRegime(updatedRegime);
    } else {
      // Selected regime was deleted, select first available
      setSelectedRegime(ignitionRegimes[0]);
    }
  }, [ignitionRegimes]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [regimeToEdit, setRegimeToEdit] = useState<IgnitionRegime | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRegimeSelect = (regime: IgnitionRegime) => {
    handleClose();
    setSelectedRegime(regime);
  };

  const handleEditRegime = (regime: IgnitionRegime) => {
    setRegimeToEdit(regime);
    setEditModalOpen(true);
  };

  const handleEditSubmit = async (name: string) => {
    if (regimeToEdit) {
      await updateRegime({ regimeId: regimeToEdit.id, name });
    }
  };

  // Remove the row at once (query cache), offer Undo for six seconds, and send
  // the delete when the window closes. The selection effect above moves to the
  // first remaining regime.
  const handleDeleteRegime = (regime: IgnitionRegime) => {
    const previous = queryClient.getQueryData<IgnitionRegime[]>(regimesKey);
    if (previous) {
      queryClient.setQueryData(
        regimesKey,
        previous.filter((r) => r.id !== regime.id)
      );
    }
    scheduleDelete({
      text: `Deleted regime “${regime.name}”`,
      commit: () => deleteRegime(regime.id),
      restore: () => {
        if (previous) queryClient.setQueryData(regimesKey, previous);
      },
    });
  };

  const regimes = ignitionRegimes ?? [];

  return (
    <div>
      <ScenarioSegmentButton
        label="Ignitions"
        icon="local_fire_department"
        value={selectedRegime?.name ?? (regimes.length ? "Select a regime" : "None")}
        meta={selectedRegime ? pluralIgnitions(selectedRegime.ignition_count) : undefined}
        open={Boolean(anchorEl)}
        onClick={handleClick}
        maxValueWidth={180}
      />
      <ScenarioPopover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        title="Ignition regimes"
        count={regimes.length}
        onNew={viewOnly ? undefined : () => setAddModalOpen(true)}
      >
        {isLoading && <StatusLine>Loading…</StatusLine>}
        {error && <StatusLine>Error: {error.message}</StatusLine>}
        {!isLoading && !error && regimes.length === 0 && (
          <ScenarioEmptyState
            icon="local_fire_department"
            title="No ignition regimes"
            body="Create a regime to choose where simulated fires start."
          />
        )}
        {regimes.length > 0 && (
          <ScenarioList role="listbox" ariaLabel="Ignition regime" maxHeight={360}>
            {regimes.map((regime) => (
              <ScenarioRow
                key={regime.id}
                selectable
                selected={regime.id === selectedRegime?.id}
                onSelect={() => handleRegimeSelect(regime)}
                name={regime.name}
                meta={
                  <>
                    <Icon sx={{ fontSize: 13 }} aria-hidden="true">
                      scatter_plot
                    </Icon>
                    {pluralIgnitions(regime.ignition_count)}
                  </>
                }
                actions={
                  viewOnly
                    ? []
                    : [{ icon: "edit", label: "Edit regime", onClick: () => handleEditRegime(regime) }]
                }
                menuItems={
                  viewOnly
                    ? []
                    : [
                        {
                          icon: "delete",
                          label: "Delete regime",
                          danger: true,
                          onClick: () => handleDeleteRegime(regime),
                        },
                      ]
                }
              />
            ))}
          </ScenarioList>
        )}
      </ScenarioPopover>
      <UndoToast pending={pending} onUndo={undo} onDismiss={dismiss} />
      <AddIgnitionRegimeModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        projectBounds={(() => {
          const bb = bboxFromBoundary(project?.data?.boundary);
          return bb ?? { top: NaN, left: NaN, bottom: NaN, right: NaN };
        })()}
        onAdd={async ({ name, mode, gridSpacingMeters }) => {
          const response = await addRegime({
            name,
            mode,
            grid_spacing_meters: mode === "grid" ? gridSpacingMeters : undefined,
          });
          setSelectedRegime(response.data);
        }}
      />
      <EditIgnitionRegimeModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setRegimeToEdit(null);
        }}
        onEdit={handleEditSubmit}
        initialName={regimeToEdit?.name || ""}
      />
    </div>
  );
}
